<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import { UserService } from '@/services/user.service';
import type { IUser } from '@/types/user.type';
import CreateUserDrawer from './CreateUserDrawer.vue';

const { t } = useI18n({ useScope: 'global' });
const router = useRouter();
const queryClient = useQueryClient();

// ── Commerces (pour filtre) ────────────────────────────────────────────────────
const { data: restaurantsData } = useQuery({
  queryKey: ['um-restaurants'],
  queryFn: async () => {
    const res = await axiosInstance.get(
      'restaurants/search?pagination=false&sortBy=name&order=ASC',
    );
    return res.data;
  },
  staleTime: 5 * 60_000,
});
const restaurants = computed<any[]>(() => {
  const d = restaurantsData.value;
  const list = d?.data ?? d?.restaurants ?? [];
  return Array.isArray(list) ? list : [];
});

// ── Liste utilisateurs ──────────────────────────────────────────────────────────
const search = ref('');
const roleFilter = ref('');
const restaurantFilter = ref('');
const page = ref(1);
const PAGE_SIZES = [10, 20, 50, 100] as const;
const pageSize = ref<(typeof PAGE_SIZES)[number]>(50);
const sortOrder = ref<'ASC' | 'DESC'>('DESC');

// Rôles à exclure systématiquement (utilisateurs "guest" et "customer" gérés ailleurs)
const EXCLUDED_ROLES = ['guest', 'customer'];

function toggleSortOrder() {
  sortOrder.value = sortOrder.value === 'DESC' ? 'ASC' : 'DESC';
}

watch([search, roleFilter, restaurantFilter, pageSize, sortOrder], () => {
  page.value = 1;
});

// ── Rôles disponibles (pour le filtre) ─────────────────────────────────────────
const { data: rolesData } = useQuery({
  queryKey: ['um-roles-filter'],
  queryFn: async () => {
    const res = await UserService.searchRolesGestion();
    return res?.data;
  },
  staleTime: 5 * 60_000,
});
const roleOptions = computed<any[]>(() =>
  (rolesData.value?.data ?? []).filter(
    (r: any) => !EXCLUDED_ROLES.includes((r.code ?? '').toLowerCase()),
  ),
);
// Rôles envoyés à l'API quand aucun rôle précis n'est sélectionné dans le filtre :
// tous les rôles disponibles, à l'exclusion de guest/customer.
const allowedRoleCodes = computed<string[]>(() =>
  roleOptions.value.map((r: any) => r.code).filter(Boolean),
);

const {
  data: usersData,
  isPending: usersLoading,
  isError: usersError,
  error: usersErrorObj,
} = useQuery({
  queryKey: computed(() => [
    'um-users',
    page.value,
    pageSize.value,
    search.value,
    roleFilter.value,
    restaurantFilter.value,
    sortOrder.value,
    allowedRoleCodes.value.join(','),
  ]),
  queryFn: async () => {
    const res = await UserService.searchUsersGestion({
      q: search.value.trim() || undefined,
      role: roleFilter.value || allowedRoleCodes.value,
      restaurantId: restaurantFilter.value || undefined,
      page: page.value,
      pageSize: pageSize.value,
      sortOrder: sortOrder.value,
    });
    return res?.data;
  },
  enabled: computed(() => allowedRoleCodes.value.length > 0),
});
const usersErrorMessage = computed(() => {
  const err = usersErrorObj.value as any;
  const msgs = err?.response?.data?.message;
  return Array.isArray(msgs) ? msgs.join(' · ') : msgs || err?.message || '';
});

const users = computed<IUser[]>(() => usersData.value?.data ?? []);
const userTotal = computed(() => usersData.value?.total ?? 0);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(userTotal.value / pageSize.value)),
);
const visiblePages = computed(() => {
  const total = totalPages.value;
  const cur = page.value;
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const start = Math.max(1, Math.min(cur - 2, total - 4));
  return Array.from({ length: Math.min(5, total) }, (_, i) => start + i);
});

const roleDropdownOpen = ref(false);
const roleSearch = ref('');
const filteredRoleOptions = computed(() =>
  roleOptions.value.filter((r) =>
    (r.label ?? r.code ?? '')
      .toLowerCase()
      .includes(roleSearch.value.toLowerCase()),
  ),
);
function selectRole(code: string) {
  roleFilter.value = code;
  roleDropdownOpen.value = false;
  roleSearch.value = '';
}
function clearRole() {
  roleFilter.value = '';
  roleSearch.value = '';
  roleDropdownOpen.value = false;
}
const selectedRoleLabel = computed(() => {
  if (!roleFilter.value) return '';
  const r = roleOptions.value.find((r) => r.code === roleFilter.value);
  return r ? (r.label ?? r.code) : roleFilter.value;
});

const restaurantDropdownOpen = ref(false);
const restaurantSearch = ref('');
const filteredRestaurantOptions = computed(() =>
  restaurants.value.filter((r) =>
    (r.designation ?? '')
      .toLowerCase()
      .includes(restaurantSearch.value.toLowerCase()),
  ),
);
function selectRestaurant(id: string) {
  restaurantFilter.value = id;
  restaurantDropdownOpen.value = false;
  restaurantSearch.value = '';
}
function clearRestaurant() {
  restaurantFilter.value = '';
  restaurantSearch.value = '';
  restaurantDropdownOpen.value = false;
}
const selectedRestaurantLabel = computed(() => {
  if (!restaurantFilter.value) return '';
  const r = restaurants.value.find((r) => r.id === restaurantFilter.value);
  return r ? r.designation : restaurantFilter.value;
});

// ── Helpers d'affichage ──────────────────────────────────────────────────────
function userDisplayName(u: IUser) {
  const full = [u.firstname, u.lastname].filter(Boolean).join(' ').trim();
  return full || u.email || 'CNC';
}
function userInitials(u: IUser) {
  const name = [u.firstname, u.lastname].filter(Boolean).join(' ').trim();
  if (name) {
    const parts = name.split(' ');
    return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
  }
  if (u.email) return u.email.slice(0, 2).toUpperCase();
  return 'CN';
}
function userColor(_letter: string) {
  return '#0b1320';
}
function roleCodeColor(code: string) {
  if (!code) return '#64748b';
  const c = code.toLowerCase();
  if (c.includes('admin.system') || c.includes('admin-system'))
    return '#ef4444';
  if (c.includes('admin')) return '#f97316';
  if (c.includes('manager')) return '#3b82f6';
  if (c.includes('staff')) return '#10b981';
  if (c.includes('guest')) return '#94a3b8';
  return '#64748b';
}

// Priorité d'affichage par rôle : les rôles d'administration/gestion
// remontent en premier, "guest" reste toujours en dernier.
const ROLE_PRIORITY: Record<string, number> = {
  'admin-system': 0,
  admin: 1,
  'store-manager': 2,
  'pos-manager': 3,
  manager: 4,
  staff: 5,
  guest: 99,
};
function userRolePriority(u: IUser) {
  const codes = (u.roles ?? []).map((r) => r.code);
  if (!codes.length) return 98;
  const priorities = codes.map((c) => ROLE_PRIORITY[c] ?? 50);
  return Math.min(...priorities);
}
const sortedUsers = computed(() =>
  [...users.value].sort((a, b) => userRolePriority(a) - userRolePriority(b)),
);

// ── Drawer de création + navigation vers la fiche détail ───────────────────────
const createDrawerOpen = ref(false);

function openCreateDrawer() {
  createDrawerOpen.value = true;
}
function closeCreateDrawer() {
  createDrawerOpen.value = false;
}
function openUserDetail(userId: string) {
  router.push(`/gestion-utilisateurs/${userId}`);
}
function onUserCreated(userId: string) {
  queryClient.invalidateQueries({ queryKey: ['um-users'] });
  openUserDetail(userId);
}
</script>

<template>
  <div
    class="um"
    @click="
      roleDropdownOpen = false;
      restaurantDropdownOpen = false;
    "
  >
    <!-- Header -->
    <div class="um__header">
      <div>
        <h1 class="um__title">{{ t('userManagement.title') }}</h1>
        <p class="um__subtitle">{{ t('userManagement.subtitle') }}</p>
      </div>
      <button class="um__btn um__btn--primary" @click="openCreateDrawer">
        <i class="pi pi-plus" /> {{ t('userManagement.addUser') }}
      </button>
    </div>

    <!-- Filters -->
    <div class="um__filters">
      <div class="um__search-wrap">
        <i class="pi pi-search um__search-icon" />
        <input
          v-model="search"
          class="um__search"
          :placeholder="t('userManagement.searchPlaceholder')"
        />
      </div>

      <div class="um__dropdown-wrap" @click.stop>
        <button
          class="um__dropdown-btn"
          :class="{ 'um__dropdown-btn--active': roleFilter }"
          @click="roleDropdownOpen = !roleDropdownOpen"
        >
          <i class="pi pi-shield" />
          <span>{{ selectedRoleLabel || t('userManagement.allRoles') }}</span>
          <i v-if="roleFilter" class="pi pi-times" @click.stop="clearRole" />
          <i v-else class="pi pi-chevron-down" />
        </button>
        <Transition name="um-drop">
          <div v-if="roleDropdownOpen" class="um__dropdown-panel">
            <div class="um__dropdown-search-wrap">
              <i class="pi pi-search" />
              <input
                v-model="roleSearch"
                class="um__dropdown-search"
                :placeholder="t('userManagement.create.searchRole')"
                @click.stop
              />
            </div>
            <div class="um__dropdown-list">
              <button
                class="um__dropdown-item"
                :class="{ 'um__dropdown-item--active': !roleFilter }"
                @click="clearRole"
              >
                {{ t('userManagement.allRoles') }}
              </button>
              <button
                v-for="r in filteredRoleOptions"
                :key="r.id"
                class="um__dropdown-item"
                :class="{ 'um__dropdown-item--active': roleFilter === r.code }"
                @click="selectRole(r.code)"
              >
                {{ r.label ?? r.code }}
              </button>
            </div>
          </div>
        </Transition>
      </div>

      <div class="um__dropdown-wrap" @click.stop>
        <button
          class="um__dropdown-btn"
          :class="{ 'um__dropdown-btn--active': restaurantFilter }"
          @click="restaurantDropdownOpen = !restaurantDropdownOpen"
        >
          <i class="pi pi-shop" />
          <span>{{
            selectedRestaurantLabel || t('userManagement.allStores')
          }}</span>
          <i
            v-if="restaurantFilter"
            class="pi pi-times"
            @click.stop="clearRestaurant"
          />
          <i v-else class="pi pi-chevron-down" />
        </button>
        <Transition name="um-drop">
          <div v-if="restaurantDropdownOpen" class="um__dropdown-panel">
            <div class="um__dropdown-search-wrap">
              <i class="pi pi-search" />
              <input
                v-model="restaurantSearch"
                class="um__dropdown-search"
                :placeholder="t('userManagement.searchStore')"
                @click.stop
              />
            </div>
            <div class="um__dropdown-list">
              <button
                class="um__dropdown-item"
                :class="{ 'um__dropdown-item--active': !restaurantFilter }"
                @click="clearRestaurant"
              >
                {{ t('userManagement.allStores') }}
              </button>
              <button
                v-for="r in filteredRestaurantOptions"
                :key="r.id"
                class="um__dropdown-item"
                :class="{
                  'um__dropdown-item--active': restaurantFilter === r.id,
                }"
                @click="selectRestaurant(r.id)"
              >
                {{ r.designation }}
              </button>
            </div>
          </div>
        </Transition>
      </div>

      <select v-model.number="pageSize" class="um__page-size-select">
        <option v-for="ps in PAGE_SIZES" :key="ps" :value="ps">
          {{ ps }} {{ t('userManagement.perPage') }}
        </option>
      </select>

      <button
        class="um__sort-btn"
        :title="t('userManagement.sortByDate')"
        @click="toggleSortOrder"
      >
        <i
          :class="
            sortOrder === 'DESC'
              ? 'pi pi-sort-amount-down'
              : 'pi pi-sort-amount-up'
          "
        />
        {{ t('userManagement.sortByDate') }}
      </button>

      <span class="um__count"
        >{{ userTotal }} {{ t('userManagement.results') }}</span
      >
    </div>

    <!-- Cards grid -->
    <div v-if="usersLoading" class="um__grid">
      <div v-for="i in 6" :key="i" class="um__card um__card--sk" />
    </div>
    <div v-else-if="usersError" class="um__empty">
      <i class="pi pi-exclamation-circle" />
      <p>
        {{ usersErrorMessage || 'Erreur lors du chargement des utilisateurs.' }}
      </p>
    </div>
    <div v-else-if="!sortedUsers.length" class="um__empty">
      <i class="pi pi-users" />
      <p>{{ t('userManagement.empty') }}</p>
    </div>
    <div v-else class="um__grid">
      <button
        v-for="u in sortedUsers"
        :key="u.id"
        class="um__card"
        @click="openUserDetail(u.id)"
      >
        <img
          v-if="u.photoUrl"
          :src="u.photoUrl"
          class="um__card-avatar um__card-avatar--img"
          alt=""
        />
        <div
          v-else
          class="um__card-avatar"
          :style="{ background: userColor(userInitials(u)[0]) }"
        >
          {{ userInitials(u) }}
        </div>
        <div class="um__card-body">
          <span class="um__card-name">{{ userDisplayName(u) }}</span>
          <span v-if="u.email" class="um__card-email">{{ u.email }}</span>
          <div class="um__card-roles">
            <span
              v-for="r in (u.roles ?? []).slice(0, 2)"
              :key="r.id"
              class="um__role-chip"
              :style="{
                background: roleCodeColor(r.code) + '1a',
                color: roleCodeColor(r.code),
                borderColor: roleCodeColor(r.code) + '40',
              }"
              >{{ r.code }}</span
            >
            <span
              v-if="(u.roles ?? []).length > 2"
              class="um__role-chip um__role-chip--more"
              :title="
                (u.roles ?? [])
                  .slice(2)
                  .map((r) => r.code)
                  .join(', ')
              "
            >
              +{{ (u.roles ?? []).length - 2 }}
            </span>
            <span
              v-if="!(u.roles ?? []).length"
              class="um__role-chip um__role-chip--none"
            >
              {{ t('userManagement.noRole') }}
            </span>
          </div>
        </div>
        <i class="pi pi-chevron-right um__card-chev" />
      </button>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="um__pagination">
      <button class="um__pg-btn" :disabled="page === 1" @click="page = 1">
        <i class="pi pi-angle-double-left" />
      </button>
      <button class="um__pg-btn" :disabled="page === 1" @click="page--">
        <i class="pi pi-chevron-left" />
      </button>
      <button
        v-for="p in visiblePages"
        :key="p"
        class="um__pg-btn"
        :class="{ 'um__pg-btn--active': p === page }"
        @click="page = p"
      >
        {{ p }}
      </button>
      <button
        class="um__pg-btn"
        :disabled="page === totalPages"
        @click="page++"
      >
        <i class="pi pi-chevron-right" />
      </button>
      <button
        class="um__pg-btn"
        :disabled="page === totalPages"
        @click="page = totalPages"
      >
        <i class="pi pi-angle-double-right" />
      </button>
    </div>

    <CreateUserDrawer
      :open="createDrawerOpen"
      @close="closeCreateDrawer"
      @created="onUserCreated"
    />
  </div>
</template>

<style scoped>
.um {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .um {
    padding: 1.5rem;
  }
}

.um__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.um__title {
  margin: 0 0 0.15rem;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--nxt-on-surface);
}
.um__subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}

.um__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    filter 0.15s,
    opacity 0.15s;
}
.um__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.35);
}
.um__btn--primary:hover {
  background: #e6c109;
}

/* Filters */
.um__filters {
  position: relative;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  padding: 0.875rem 1rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.um__search-wrap {
  position: relative;
  flex: 1;
  min-width: 12rem;
}
.um__search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.um__search {
  width: 100%;
  height: 2.5rem;
  padding: 0 0.875rem 0 2.25rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
}
.um__search:focus {
  border-color: #ffd60a;
}

.um__page-size-select {
  height: 2.5rem;
  padding: 0 2rem 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}

.um__sort-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.um__sort-btn:hover {
  border-color: #ffd60a;
}

.um__dropdown-wrap {
  position: relative;
}
.um__dropdown-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.um__dropdown-btn--active {
  border-color: #ffd60a;
  color: #0b1320;
}
.um__dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 200;
  min-width: 16rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.um__dropdown-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.um__dropdown-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
}
.um__dropdown-list {
  max-height: 220px;
  overflow-y: auto;
  padding: 0.375rem;
}
.um__dropdown-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
}
.um__dropdown-item:hover {
  background: var(--nxt-surface-mid);
}
.um__dropdown-item--active {
  background: var(--nxt-surface-mid);
  font-weight: 700;
  color: #0b1320;
}

.um-drop-enter-active,
.um-drop-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.um-drop-enter-from,
.um-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.um__count {
  margin-left: auto;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}

/* Grid & cards */
.um__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}
.um__card {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1.25rem 1.5rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}
.um__card:hover {
  border-color: rgba(255, 214, 10, 0.4);
}
.um__card--sk {
  height: 5rem;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: um-shimmer 1.4s infinite;
  cursor: default;
}
@keyframes um-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.um__card-avatar {
  width: 2.75rem;
  height: 2.75rem;
  min-width: 2.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  font-size: 0.875rem;
}
.um__card-avatar--img {
  object-fit: cover;
  flex-shrink: 0;
}
.um__card-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.um__card-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.um__card-email {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.um__card-roles {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.25rem;
}
.um__card-chev {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  flex-shrink: 0;
}

.um__role-chip {
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 0.3rem;
  border: 1px solid;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.um__role-chip--none {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-muted);
  border-color: var(--nxt-outline-variant);
}
.um__role-chip--more {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-variant);
  border-color: var(--nxt-outline-variant);
  cursor: default;
}

.um__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  color: var(--nxt-on-surface-muted);
}
.um__empty i {
  font-size: 2rem;
  opacity: 0.4;
}

/* Pagination */
.um__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
}
.um__pg-btn {
  min-width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-family: inherit;
  cursor: pointer;
}
.um__pg-btn--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
  font-weight: 700;
}
.um__pg-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
</style>

<style>
[data-theme='dark'] .um__filters,
[data-theme='dark'] .um__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .um__card:hover {
  border-color: rgba(255, 214, 10, 0.35);
}
[data-theme='dark'] .um__search,
[data-theme='dark'] .um__page-size-select,
[data-theme='dark'] .um__sort-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .um__dropdown-panel {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .um__dropdown-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .um__dropdown-item:hover,
[data-theme='dark'] .um__dropdown-item--active {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .um__search:focus {
  border-color: #ffd60a;
}
[data-theme='dark'] .um__pg-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
</style>
